/**
 * 根级错误边界。
 *
 * 一份结构残缺的模板（AI 生成的、手改过的、旧版本存下来的）会让渲染在
 * React 树里抛错，整个侧边栏被卸载成一片空白 —— 客户看到的是「插件坏了」，
 * 而且没有任何可操作的出路，连换个模板都做不到。这里兜住，给出错误原因和
 * 一个「回首页」按钮，至少能自救。
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** 点「回首页」时调用，通常是把视图切回 home 并清掉正在编辑的模板。 */
  onReset?: () => void;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // 留在控制台，方便客户截图给我们看
    console.error('[print-plugin] 渲染出错', error, info.componentStack);
  }

  private reset = (): void => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div style={{ padding: 20, fontSize: 13, lineHeight: 1.7, color: 'var(--ink-1, #2f2b28)' }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>插件遇到了一个错误</div>
        <div style={{ color: 'var(--ink-2, #5a5552)', marginBottom: 12 }}>
          多半是某个模板的结构有问题（常见于 AI 生成或很早以前建的模板）。
          点下面的按钮回到首页换一个模板，或把这段信息发给我们。
        </div>
        <pre
          style={{
            background: 'var(--surface-2, #f6f4f0)',
            borderRadius: 8,
            padding: 10,
            fontSize: 11,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            maxHeight: 160,
            overflow: 'auto',
            margin: '0 0 12px',
          }}
        >
          {error.message || String(error)}
        </pre>
        <button
          onClick={this.reset}
          style={{
            height: 30,
            padding: '0 14px',
            borderRadius: 6,
            border: '1px solid var(--border, #ddd8d0)',
            background: 'var(--accent, #c26442)',
            color: '#fff',
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          回首页
        </button>
      </div>
    );
  }
}
